import {testIdProps} from '@github-ui/test-id-props'
import {type Ref, useCallback, useLayoutEffect, useRef} from 'react'

import styles from './TaillightsCaretVisual.module.css'

export type TaillightsCaretVisualGeometry = {
  top: number
  left: number
  height: number
}

export type TaillightsCaretVisualProps = {
  geometry: TaillightsCaretVisualGeometry | null
  visible: boolean
  /** Blinks like a text caret, unless the user prefers reduced motion. */
  blink?: boolean
  ref?: Ref<HTMLDivElement>
  testId?: string
}

const blinkMs = 1400
// Visible for the first half of each cycle, hidden for the second, with no fade.
const blinkKeyframes: Keyframe[] = [{opacity: 1}, {opacity: 1, offset: 0.5}, {opacity: 0, offset: 0.5}, {opacity: 0}]

/** Draws a decorative caret at caller-supplied coordinates without owning input, focus or selection. */
export function TaillightsCaretVisual({
  geometry,
  visible,
  blink = false,
  ref,
  testId = 'code-view-caret',
}: TaillightsCaretVisualProps) {
  const nodeRef = useRef<HTMLDivElement | null>(null)
  const animationRef = useRef<Animation | null>(null)
  const attach = useCallback(
    (node: HTMLDivElement | null) => {
      nodeRef.current = node
      if (typeof ref === 'function') return ref(node)
      if (ref) ref.current = node
    },
    [ref],
  )
  const shown = visible && geometry !== null

  // A script animation rather than CSS: React listens for animationiteration at its root, and Chrome ticks the main
  // thread for every CSS animation while such a listener exists. This one runs on the compositor alone.
  useLayoutEffect(() => {
    const node = nodeRef.current
    if (!node || !blink || !shown || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    const animation = node.animate(blinkKeyframes, {duration: blinkMs, iterations: Infinity})
    animationRef.current = animation
    return () => {
      animation.cancel()
      animationRef.current = null
    }
  }, [blink, shown])

  // Like a native caret, it stays solid while it moves and blinks once it rests.
  const top = geometry?.top
  const left = geometry?.left
  useLayoutEffect(() => {
    if (animationRef.current) animationRef.current.currentTime = 0
  }, [top, left])

  return (
    <div
      ref={attach}
      aria-hidden="true"
      hidden={!shown}
      className={styles.TaillightsCaretVisual}
      style={geometry ? {top: geometry.top, left: geometry.left, height: geometry.height} : undefined}
      {...testIdProps(testId)}
    />
  )
}

TaillightsCaretVisual.displayName = 'TaillightsCaretVisual'
